import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Brain, Loader2, Stethoscope, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const SymptomChecker = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [symptoms, setSymptoms] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCheck = async () => {
    if (!symptoms.trim()) {
      toast.error('Please describe your symptoms');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/symptom-check`, { symptoms });
      setResult(response.data);
      toast.success('Analysis complete!');
    } catch (error) {
      toast.error('Failed to analyze symptoms');
    } finally {
      setLoading(false);
    }
  };

  // Enhanced loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
        <Navigation user={user} setUser={setUser} />
        <div className="container mx-auto px-4 py-6 sm:py-8 pt-24">
          <div className="flex items-center justify-center py-20">
            <div className="text-center max-w-md">
              <div className="relative mb-6">
                <Loader2 className="w-20 h-20 animate-spin text-teal-600 mx-auto" />
                <Stethoscope className="w-8 h-8 text-teal-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">AI Analyzing Symptoms</h3>
              <p className="text-gray-600 mb-4">Our medical AI is reviewing your symptoms...</p>
              
              <div className="space-y-3 text-left bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-gray-600">Processing symptom description</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                  <span className="text-sm text-gray-600">Cross-referencing medical database</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                  <span className="text-sm text-gray-600">Generating recommendations</span>
                </div>
              </div>
              
              <p className="text-xs text-gray-500 mt-4">
                This may take 5-10 seconds • Cached results load instantly
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 sm:py-8 pt-24 " data-testid="symptom-checker-page">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center mx-auto mb-4">
              <Brain className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">{t('aiFeatures.symptomChecker')}</h1>
            <p className="text-gray-600 text-lg">{t('symptomChecker.subtitle')}</p>
          </div>

          <Card className="p-8 bg-white shadow-xl mb-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Describe your symptoms in detail
                </label>
                <Textarea
                  placeholder="E.g., I have a headache, fever, and sore throat for 2 days..."
                  value={symptoms}
                  onChange={(e) => setSymptoms(e.target.value)}
                  className="min-h-32 text-base"
                  data-testid="symptoms-input"
                />
              </div>
              <Button
                onClick={handleCheck}
                disabled={loading}
                className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-6 text-lg font-semibold hover:shadow-xl transition-all"
                data-testid="check-symptoms-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  'Analyze Symptoms'
                )}
              </Button>
            </div>
          </Card>

          {result && (
            <div className="space-y-6">
              <Card className="p-8 bg-white shadow-xl" data-testid="analysis-result">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Analysis</h3>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{result.analysis}</p>
              </Card>

              <Card className="p-8 bg-gradient-to-br from-teal-50 to-cyan-50 border-2 border-teal-200 shadow-xl">
                <h3 className="text-2xl font-bold text-teal-800 mb-4">Recommendations</h3>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{result.recommendations}</p>
              </Card>

              <Card className="p-6 bg-yellow-50 border-2 border-yellow-200">
                <p className="text-sm text-yellow-800">
                  <strong>Disclaimer:</strong> This is an AI-powered preliminary analysis and should not replace professional medical advice. 
                  Please consult with a healthcare professional for proper diagnosis and treatment.
                </p>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SymptomChecker;
